import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Typography from '@material-ui/core/Typography'

import BigNumber from 'bignumber.js'

import { ConditionalRender, ConfirmationDialog } from 'components'
import FormMixin from 'containers/shared/FormMixin'
import { AllocationActions, UserSelectionActions } from 'actionsets'
import StyleSheet, { colorPalette } from 'styles'
import { User } from 'containers'
import moment from 'moment-timezone'
import {capitalize} from 'utils'
import {FormControlLabel, Radio, RadioGroup} from '@material-ui/core'

export class AllocationForm extends FormMixin(Component) {
  static propTypes: {
    dealSheet: PropTypes.object.isRequired,
    editAllocationIds: PropTypes.array.isRequired,
    bulkParams: PropTypes.array.isRequired,
    onDone: PropTypes.func.isRequired
  }

  static defaultProps = {

  }

  constructor(props){
    super(props)
    this.state = {
      showAllocationFormDialog: true,
    }
    this.onSaveRoute = null

    AllocationActions.bindActions(this)
    UserSelectionActions.bindActions(this, 'userSelect')
  }

  componentDidMount = () => {
    if(this.createMode) {
      let defaultTimezone = this.props.dealSheet.timezone || moment.tz.guess()
      this.setAttribute('timezone', this.attribute('timezone') || defaultTimezone)
    }
  }

  get saveActionName() {
    return this.createMode ? 'bulkInsert' : 'bulkUpdate'
  }

  get formObject(){
    return {amountOf: 'units', ...(this.props.bulkParams || {dealSheetId: this.dealSheetId})}
  }

  get formObjectName(){
    return `${capitalize(this.allocationName)} Form`
  }

  get createMode(){
    return (this.props.editAllocationIds.length === 0)
  }

  get updatedFormObject(){
    const updatedFormObject = {}
    const amountOf = this.amountOf
    let { rate, amount, startDate, endDate, timezone, organisationCommission: { coinPercentage, tokenPercentage } = {} } = super.updatedFormObject
    if (this.props.dealSheet.typeOfIssuance === 'equity') rate = 1;
    Object.entries({
      rate,
      amount: amountOf === 'units' ? amount : this.derivedAmount,
      startDate,
      endDate,
      timezone,
      organisationCommission: {
        coinPercentage, tokenPercentage
      },
      dealSheetId: this.dealSheetId,
      ids:         this.createMode ? this.props.newInvestorIds : this.props.editAllocationIds
    }).forEach(([key, value]) => {
      if(![undefined,null, ""].includes(value)){
        updatedFormObject[key] = value
      }
    })

    return updatedFormObject
  }

  get ticker(){
    return this.props.dealSheet.ticker || 'Unit'
  }

  get currencyCode(){
    return this.props.dealSheet.currency.code
  }

  get amountOf() {
    return this.attribute('amountOf')
  }

  get derivedAmount(){
    if(!this.attribute('amount') || !this.attribute('rate')) {
      return ''
    }

    const [amount, rate] = [new BigNumber(this.attribute('amount')), new BigNumber(this.attribute('rate'))]
    if (this.amountOf === 'units') {
      return amount.div(rate).round(16).toString()
    } else {
      return amount.times(rate).round(16).toString()
    }
  }

  get derivedAmountText(){
    const derivedAmount = this.derivedAmount
    const amountOf = this.amountOf
    return derivedAmount && `${this.derivedAmount} ${amountOf === 'units' ? this.currencyCode : this.ticker}`
  }

  get editorCountText() {
    if (this.createMode) {
      return `Creating ${this.props.newInvestorIds.length} ${this.allocationName}s`
    } else {
      return `Editing ${this.props.editAllocationIds.length} ${this.allocationName}s`
    }
  }

  get dealSheetId(){
    return this.props.dealSheet.id
  }

  get allocationName() {
    return this.props.dealSheet.typeOfIssuance === 'product' ? 'quote' : 'allocation'
  }

  async save(){
    if(this.createMode && this.props.newInvestorIds.length === 0){
      throw new Error(`You must select at least one user to create an ${this.allocationName} for`)
    }

    return await super.save()
  }

  showAllocationFormDialog = () => {
    this.setState({showAllocationFormDialog: true})
  }

  hideAllocationFormDialog = () => {
    this.setState({showAllocationFormDialog: false})
  }

  showInvestorsDialog = async () => {
    if (this.createMode) {
      this.hideAllocationFormDialog()
      try{
        const userIds = await this.actions.userSelect.makeSelection({
          title: `Create ${capitalize(this.allocationName)}s`,
          cancelActionName: 'Back',
          actionName: 'Add'
        })
        this.actions.setNewInvestorIds({ids: userIds, params: this.props.bulkParams})
      } finally {
        this.showAllocationFormDialog()
      }
    }
  }

  done = (newAllocationsCreated) => {
    this.props.onDone(newAllocationsCreated)
  }

  createNewAllocation = async () => {
    this.clearError()
    try {
      await this.save()
      this.done(true)
    } catch (err) {
      this.setError(err.message || `Could not save ${this.allocationName}.`)
    }
  }

  setError = (errorMessage) => {
    this.setState({error: errorMessage})
  }

  clearError = () => {
    this.setState({error: null})
  }

  getLabels(defaultLabel, attributeName){
    const attribute = this.deepGet(attributeName, this.formObject)
    if(!attribute && this.props.bulkParams && this.props.editAllocationIds.length > 1){
      return { label: defaultLabel, helperText: this.getError(attributeName) || <small className={styles.warningText}>Warning: Values for {defaultLabel} are not shared for all selected {this.allocationName}s</small> }
    }else{
      return {label: defaultLabel, helperText: this.getError(attributeName) || ''}
    }
  }

  renderForm = () => {
    if (!this.props.dealSheet.id){
      return false
    }

    const isEquity = this.props.dealSheet.typeOfIssuance === 'equity'
    const amountOf = this.amountOf
    const amountLabel = amountOf === 'value' ? `Value (${this.currencyCode})` : `Number of tokens (${this.ticker})`
    const derivedLabel = amountOf === 'value' ? `Number of tokens (${this.ticker})` : `Value (${this.currencyCode})`
    const { LargeNumberField, NumberField, TimezoneField, TextField, DateTimeField } = this
    return(
      <div className={styles.root}>
        <ConditionalRender className={styles.errorText} requires={!!this.state.error} component={Typography}>
          {this.state.error}
        </ConditionalRender>
        <TextField
          error={!!this.getError('investorAccount')}
          helperText={this.getError('investorAccount')}
          fullWidth
          value={this.editorCountText}
          margin="normal"
          label="Investors"
          inputRef={rf => rf ? rf.readOnly = true : null}
          onClick={() => this.showInvestorsDialog()}
        />
        {isEquity ? (
          <LargeNumberField {...this.getLabels('Value', 'value')} attribute='amount'/>
        ) : (
          <>
            <RadioGroup row name="amountOf" value={amountOf} onChange={this.attributeChanged('amountOf')}>
              <FormControlLabel value="value" control={<Radio />} label={'Nominal Value'} />
              <FormControlLabel value="units" control={<Radio />} label={'Number of Units'} />
            </RadioGroup>
            <LargeNumberField
              {...this.getLabels(amountLabel, 'amount')}
              attribute='amount'
            />
            <NumberField
              {...this.getLabels(`Rate (${this.ticker}/${this.currencyCode})`, 'rate')}
              attribute='rate'
            />
            <TextField disabled label={derivedLabel} value={this.derivedAmountText}/> }
          </>
        )}
        <NumberField
          {...this.getLabels(`Commission (%${this.currencyCode})`, 'organisationCommission.coinPercentage')}
          attribute='organisationCommission.coinPercentage'
        />
        {!isEquity && (
          <NumberField
            {...this.getLabels(`Commission (%${this.ticker})`, 'organisationCommission.tokenPercentage')}
            attribute='organisationCommission.tokenPercentage'
          />
        )}
        <TimezoneField {...this.getLabels('Timezone', 'timezone')} attribute="timezone"/>
        <DateTimeField
          {...this.getLabels('Start Date', 'startDate')}
          timeLabel='Start time'
          showClear={false}
          attribute='startDate'
          timezone={this.attribute('timezone')}
        />
        <DateTimeField
          {...this.getLabels('End Date', 'endDate')}
          timeLabel='End time'
          showClear={false}
          attribute='endDate'
          timezone={this.attribute('timezone')}
          defaultTime={'23:59:59'}
        />
      </div>
    )
  }

  renderInvestors = () => {
    return (
      <div>Investors</div>
    )
  }

  render = () => {
    return (
      <Fragment>
        {
          this.state.showAllocationFormDialog &&
          <ConfirmationDialog title={`Create ${capitalize(this.allocationName)}s`} open={true} onCancel={() => this.done(false)} onConfirm={this.createNewAllocation} cancelLabel='Cancel' confirmLabel='Save'>
            { this.renderForm() }
          </ConfirmationDialog>
        }
        {
          this.createMode &&
          <User.UserSelect selection={this.props.newInvestorIds} prefilters={{access_level: 'member', account_type: 'investor'}}/>
        }
      </Fragment>
    )
  }
}

const styles = StyleSheet({
  root: {

  },
  actionLink: {
    color: colorPalette.tertiary[0].hex(),
    // font-family: "Gotham Bold";
    fontWeight: 'bold',
    fontSize: '14px',
    lineHeight: '13px',

    marginTop: '45px',
    marginBottom: '0px'
  },
  errorText: {
    color: 'red',
    fontWeight: 'bold'
  }
}, {classNamePrefix: 'AllocationForm-'})

export default connect(({allocations, dealSheets: {dealSheet}}) => {
  return { ...allocations, dealSheet }
})(AllocationForm)